/**
 * What a list draws as columns and where its row link sits, decided from the manifest and the rows read: an internal
 * id (a uuid, or `aud_` and 32 hex digits) is the record's address, not something a person reads, so no column shows
 * it and the row links on a name instead. A CIS number (ORD-K48193) is read by people and stays.
 */
import type { ChildRow, ManifestColumn, ManifestList } from "./contract";

/**
 * A uuid, or a prefixed or bare 32-digit hex id (`aud_76d5d3d1e2cf4eb99398f30ae0e1b989`) as modules mint them.
 * Claude's record heading uses the same shape (`_OPAQUE_ID` in `apps/api/app/operational_mcp/present_record.py`, held
 * by `record-presentation.parity.json`).
 */
const OPAQUE_ID =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?:[a-z]{2,10}_)?[0-9a-f]{32})$/i;

export function isOpaqueId(value: unknown): boolean {
  return typeof value === "string" && OPAQUE_ID.test(value);
}

/**
 * Whether a record's own identifier is one a person reads. A declared uuid never is; a `field` identifier is not when
 * its value is an opaque id (an audience's `aud_…`). The record is then named by its title.
 */
export function isIdentifierReadable(
  identifier: { kind: string },
  value: unknown,
): boolean {
  return identifier.kind !== "uuid" && !isOpaqueId(value);
}

/**
 * The fields of a list's columns that hold internal ids and so are not drawn: the key column of an object declared
 * by uuid, and any column whose every value on the page is an opaque id. A column that names another record (`ref`)
 * is drawn as that record's link, so it stays. Never every column: a list of ids alone keeps its columns.
 */
export function idColumnFields(
  list: ManifestList,
  identifierKind: string,
  items: readonly ChildRow[],
): Set<string> {
  const hidden = new Set<string>();
  for (const column of list.columns) {
    if (column.ref) continue;
    const isDeclaredUuidKey =
      column.field === list.key_field && identifierKind === "uuid";
    const values = items
      .map((item) => item[column.field])
      .filter((value) => value !== null && value !== undefined && value !== "");
    if (isDeclaredUuidKey || (values.length > 0 && values.every(isOpaqueId)))
      hidden.add(column.field);
  }
  return hidden.size < list.columns.length ? hidden : new Set();
}

/**
 * The column a row's link sits on when `linkField` (list-includes.ts) names a column that is not drawn, or that the list
 * does not have (a catalogue's id is its address, never a column): the list's title column, else its first drawn
 * column that is plain text. Null links nowhere (the row itself still opens).
 */
export function visibleLinkField(
  list: ManifestList,
  linkField: string | null,
  hidden: ReadonlySet<string>,
): string | null {
  if (
    linkField === null ||
    (!hidden.has(linkField) && list.columns.some((c) => c.field === linkField))
  )
    return linkField;
  const shown = list.columns.filter((c) => !hidden.has(c.field));
  const title = shown.find((c) => c.field === list.title_field);
  return (title ?? shown.find((c) => c.kind === "text"))?.field ?? null;
}

/** Kinds that read as one short token and stay on one line however narrow the list is. */
const ONE_LINE_KINDS: ReadonlySet<ManifestColumn["kind"]> = new Set([
  "number",
  "money",
  "date",
  "datetime",
  "state",
  "boolean",
  "identifier",
  "link",
]);

/** Whether a column's text may wrap within a width (and show its full value on hover), not stay on one line. */
export function isWrappingColumn(
  column: Pick<ManifestColumn, "kind">,
): boolean {
  return !ONE_LINE_KINDS.has(column.kind);
}
